import type { VFXEffectConfig } from '../../VFXEffectRegistry';

const HIT_PHYSICAL: VFXEffectConfig = {
  id: 'hit_physical',
  name: 'Physical Hit',
  category: 'burst',
  primitives: [
    { type: 'particles', particleEffectId: 'hit_spark', particleCount: 5 },
    { type: 'particles', particleEffectId: 'sparkle', particleCount: 3 },
    { type: 'entity_override', entityTint: 0xFF0000, tween: { duration: 100 } },
    // Shape fallback — always-visible red spark burst
    { type: 'circle', radius: 8, color: 0xFF4444, alpha: 0.8, depth: 95,
      tween: { alpha: 0, scale: 2.5, duration: 200, ease: 'Power2' },
      scatter: { count: 4, radius: 18 } },
    { type: 'circle', radius: 15, color: 0xFF2200, alpha: 0.5, depth: 93,
      tween: { alpha: 0, scale: 1.8, duration: 250, ease: 'Power2' } }
  ]
};

const HIT_FIRE: VFXEffectConfig = {
  id: 'hit_fire',
  name: 'Fire Hit',
  category: 'burst',
  screenFlash: { color: 0xFF6600, duration: 150 },
  primitives: [
    { type: 'particles', particleEffectId: 'fire_burst', particleCount: 10 },
    { type: 'circle', radius: 30, color: 0xFF4500, alpha: 0.6, depth: 94, tween: { alpha: 0, scale: 2, duration: 300, ease: 'Power2' } },
    { type: 'circle', radius: 12, color: 0xFFFF00, alpha: 0.8, depth: 96, tween: { alpha: 0, scale: 1.5, duration: 200, ease: 'Power1' } },
    { type: 'circle', radius: 6, color: 0xFF6600, alpha: 0.7, depth: 95, tween: { alpha: 0, scale: 0.4, duration: 350, ease: 'Power2' }, scatter: { count: 5, radius: 25 } },
    { type: 'entity_override', entityTint: 0xFF6600, tween: { duration: 150 } }
  ]
};

const HIT_ICE: VFXEffectConfig = {
  id: 'hit_ice',
  name: 'Ice Hit',
  category: 'burst',
  screenFlash: { color: 0x88CCFF, duration: 150 },
  primitives: [
    { type: 'particles', particleEffectId: 'ice_burst', particleCount: 8 },
    { type: 'star', points: 6, innerRadius: 5, outerRadius: 20, color: 0x00FFFF, alpha: 0.7, depth: 96, tween: { alpha: 0, rotation: Math.PI, duration: 400, ease: 'Power2' } },
    { type: 'circle', radius: 18, color: 0x88CCFF, alpha: 0.5, depth: 94, tween: { alpha: 0, scale: 2, duration: 350, ease: 'Power2' } },
    { type: 'circle', radius: 3, color: 0xFFFFFF, alpha: 0.8, depth: 97, tween: { alpha: 0, scale: 0.5, duration: 250, ease: 'Power1' }, scatter: { count: 4, radius: 20 } },
    { type: 'entity_override', entityTint: 0x88CCFF, tween: { duration: 200 } }
  ]
};

const HIT_LIGHTNING: VFXEffectConfig = {
  id: 'hit_lightning',
  name: 'Lightning Hit',
  category: 'burst',
  screenFlash: { color: 0xFFFF00, duration: 100 },
  screenShake: { intensity: 8, duration: 250 },
  primitives: [
    { type: 'particles', particleEffectId: 'lightning_burst', particleCount: 6 },
    { type: 'rect', width: 4, height: 40, color: 0xFFFF00, alpha: 0.9, depth: 95, tween: { alpha: 0, scaleX: 5, duration: 200, ease: 'Power1' } },
    { type: 'circle', radius: 15, color: 0xFFFFFF, alpha: 0.8, depth: 97, tween: { alpha: 0, scale: 2, duration: 150, ease: 'Power1' } },
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 8, color: 0xFFFF88, alpha: 0.9, depth: 96, tween: { rotation: Math.PI * 0.25, alpha: 0, duration: 200, ease: 'Power2' }, scatter: { count: 3, radius: 18 } },
    { type: 'entity_override', entityTint: 0xFFFF00, tween: { duration: 150 } }
  ]
};

// Holy DAMAGE identity (2026-07-16, art-direction pass): holy damage used to
// route to hit_heal and read as a GREEN heal. Radiant gold instead — heals
// (school 'heal'/'healing') keep hit_heal.
const HIT_HOLY: VFXEffectConfig = {
  id: 'hit_holy',
  name: 'Holy Hit',
  category: 'burst',
  screenFlash: { color: 0xFFF3B0, duration: 150 },
  primitives: [
    { type: 'particles', particleEffectId: 'holy_burst', particleCount: 8 },
    // Radiant bloom.
    { type: 'circle', radius: 26, color: 0xFFD700, alpha: 0.55, depth: 94,
      tween: { alpha: 0, scale: 2.2, duration: 380, ease: 'Power2' } },
    // Cross flare — judgment beat.
    { type: 'star', points: 4, innerRadius: 5, outerRadius: 18, color: 0xFFFFFF, alpha: 0.9, depth: 96,
      tween: { rotation: Math.PI * 0.5, alpha: 0, scale: 1.5, duration: 340, ease: 'Power2' } },
    // Gold shards.
    { type: 'circle', radius: 4, color: 0xFFEE99, alpha: 0.85, depth: 95,
      tween: { alpha: 0, scale: 0.4, duration: 320, ease: 'Power2' }, scatter: { count: 5, radius: 24 } },
    { type: 'entity_override', entityTint: 0xFFD700, tween: { duration: 180 } },
  ],
};

const HIT_ARCANE: VFXEffectConfig = {
  id: 'hit_arcane',
  name: 'Arcane Hit',
  category: 'burst',
  screenFlash: { color: 0xAA00FF, duration: 150 },
  primitives: [
    { type: 'particles', particleEffectId: 'arcane_burst', particleCount: 12 },
    { type: 'circle', radius: 25, color: 0xAA00FF, alpha: 0.5, depth: 94, tween: { alpha: 0, scale: 2.5, duration: 500, ease: 'Power2' } },
    { type: 'star', points: 4, innerRadius: 5, outerRadius: 14, color: 0xCC66FF, alpha: 0.8, depth: 96, tween: { rotation: Math.PI * 0.5, alpha: 0, scale: 1.5, duration: 400, ease: 'Power2' } },
    { type: 'circle', radius: 4, color: 0xFFFFFF, alpha: 0.9, depth: 97, tween: { alpha: 0, scale: 0.3, duration: 300, ease: 'Power2' }, scatter: { count: 5, radius: 22 } },
    { type: 'entity_override', entityTint: 0xAA00FF, tween: { duration: 250 } }
  ]
};

// Nature + shadow closed the F5-013 gap (VQA sprint 2026-07-16): `getSchoolHitVFX`
// had no case for either school, so all 144 nature+shadow abilities fell to
// `default: 'hit_physical'` and flashed generic RED — reviewers measured druid
// `moonfire` at 765px of quantized (240,64,64) in the target region. Colors come
// from the ABILITY_SCHOOL_COLORS SSOT so a school recolor stays a one-file change.
// Particle ids must be verified-real: `hit_fire`/`hit_ice`/`hit_lightning` shipped
// referencing `fire_particle`/`ice_particle`/`lightning_particle`, which never
// existed (fixed to `*_burst` 2026-07-16), so those layers silently no-op'd for an
// unknown length of time. `make vfx-preflight` + schoolHitVfx.test.ts now guard it.

const HIT_NATURE: VFXEffectConfig = {
  id: 'hit_nature',
  name: 'Nature Hit',
  category: 'burst',
  screenFlash: { color: 0x44cc44, duration: 150 },
  primitives: [
    { type: 'particles', particleEffectId: 'nature_burst', particleCount: 8 },
    // Bloom out — the "growth" beat that separates nature from a physical hit.
    { type: 'circle', radius: 26, color: 0x44cc44, alpha: 0.5, depth: 94,
      tween: { alpha: 0, scale: 2.2, duration: 340, ease: 'Power2' } },
    // Leaf-ish shards flung outward.
    { type: 'star', points: 5, innerRadius: 4, outerRadius: 13, color: 0x88ff88, alpha: 0.8, depth: 96,
      tween: { rotation: Math.PI * 0.4, alpha: 0, scale: 1.4, duration: 380, ease: 'Power2' },
      scatter: { count: 4, radius: 22 } },
    { type: 'circle', radius: 5, color: 0x22aa22, alpha: 0.75, depth: 95,
      tween: { alpha: 0, scale: 0.4, duration: 300, ease: 'Power2' }, scatter: { count: 5, radius: 24 } },
    { type: 'entity_override', entityTint: 0x44cc44, tween: { duration: 180 } }
  ]
};

const HIT_SHADOW: VFXEffectConfig = {
  id: 'hit_shadow',
  name: 'Shadow Hit',
  category: 'burst',
  screenFlash: { color: 0x9933cc, duration: 150 },
  primitives: [
    { type: 'particles', particleEffectId: 'shadow_burst', particleCount: 7 },
    // Implosion-then-bruise: shadow collapses inward rather than bursting out.
    { type: 'circle', radius: 30, color: 0x6600aa, alpha: 0.55, depth: 94,
      tween: { alpha: 0, scale: 0.5, duration: 360, ease: 'Power2' } },
    { type: 'circle', radius: 16, color: 0x9933cc, alpha: 0.7, depth: 95,
      tween: { alpha: 0, scale: 1.9, duration: 300, ease: 'Power2' } },
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 11, color: 0xcc66ff, alpha: 0.85, depth: 96,
      tween: { rotation: Math.PI * 0.3, alpha: 0, duration: 320, ease: 'Power2' },
      scatter: { count: 4, radius: 20 } },
    { type: 'entity_override', entityTint: 0x9933cc, tween: { duration: 200 } }
  ]
};

const HIT_HEAL: VFXEffectConfig = {
  id: 'hit_heal',
  name: 'Heal Effect',
  category: 'burst',
  screenFlash: { color: 0x00FF00, duration: 200 },
  primitives: [
    { type: 'particles', particleEffectId: 'healing_particles', particleCount: 10 },
    { type: 'circle', radius: 30, color: 0x00FF00, alpha: 0.3, depth: 94, tween: { alpha: 0, scale: 1.5, duration: 600, ease: 'Power2' } },
    { type: 'circle', radius: 15, color: 0x88FF88, alpha: 0.6, depth: 95, tween: { alpha: 0, scale: 2, duration: 400, ease: 'Power2' } },
    { type: 'star', points: 4, innerRadius: 3, outerRadius: 8, color: 0xFFFFFF, alpha: 0.8, depth: 96, tween: { y: -15, rotation: Math.PI * 0.25, alpha: 0, duration: 500, ease: 'Power2' }, scatter: { count: 4, radius: 20 } },
    { type: 'entity_override', entityTint: 0x00FF00, tween: { duration: 400 } }
  ]
};

const HIT_CRITICAL: VFXEffectConfig = {
  id: 'hit_critical',
  name: 'Critical Hit',
  category: 'burst',
  screenShake: { intensity: 8, duration: 300 },
  // screenFlash removed 2026-07-14: the yellow full-screen flash (0xFFFF00,
  // 150ms) dominated every crit frame and washed out the ability's own cast
  // VFX (e.g. eviscerate_slash was invisible behind it). The golden star
  // burst + screen shake + hit sparks are sufficient crit feedback without
  // blinding the player. See docs/qa/KIMI_VISION_VQA_RUNBOOK.md.
  // NO floatingText here: DamagePresentationService is the single owner of the
  // damage number (confirmed server value, labeled). This def fires on the
  // OPTIMISTIC client path (CombatEventVFXHandler.onDamageDealt) — a {amount}
  // text here rendered a second, unlabeled, predicted number beside the
  // confirmed one (the "40 Fireball + 22" double-FCT bug). The crit *splash*
  // (shake/star/spark) still fires here for feel; the number waits for the
  // server. Guarded by effectDefs.invariants.test.ts.
  primitives: [
    { type: 'particles', particleEffectId: 'hit_spark', particleCount: 12 },
    // Shape fallback — golden crit burst always visible
    { type: 'star', points: 5, innerRadius: 8, outerRadius: 22, color: 0xFFDD00, alpha: 0.9, depth: 96,
      tween: { alpha: 0, scale: 2, rotation: Math.PI * 0.5, duration: 350, ease: 'Power2' } },
    { type: 'circle', radius: 20, color: 0xFFFF00, alpha: 0.6, depth: 94,
      tween: { alpha: 0, scale: 2.5, duration: 300, ease: 'Power2' } },
    { type: 'circle', radius: 5, color: 0xFFFFFF, alpha: 0.9, depth: 97,
      tween: { alpha: 0, scale: 3, duration: 200, ease: 'Power1' },
      scatter: { count: 6, radius: 30 } }
  ]
};

/**
 * Crit OVERLAY — composes WITH the school/authored hit instead of replacing
 * it (2026-07-17). Before this, a crit on an unauthored ability resolved to
 * the generic gold hit_critical and ERASED the school identity (a nature
 * crit looked identical to a fire crit). This def carries only the crit
 * grammar — shake + gold star ring + white glints — no colored fill and no
 * particle burst, so the school hit underneath keeps owning the palette.
 * hit_critical remains for defs that author it explicitly (execute,
 * kill_shot, eviscerate) and for the school-less showDamageNumber path.
 */
const CRIT_OVERLAY: VFXEffectConfig = {
  id: 'crit_overlay',
  name: 'Critical Overlay',
  category: 'burst',
  screenShake: { intensity: 8, duration: 300 },
  primitives: [
    { type: 'star', points: 5, innerRadius: 9, outerRadius: 24, color: 0xFFDD00, alpha: 0.85, depth: 98, glow: true,
      tween: { alpha: 0, scale: 2.1, rotation: Math.PI * 0.5, duration: 340, ease: 'Power2' } },
    { type: 'star', points: 4, innerRadius: 4, outerRadius: 10, color: 0xFFFFFF, alpha: 0.9, depth: 99,
      tween: { alpha: 0, scale: 2.6, rotation: -Math.PI * 0.4, duration: 260, ease: 'Power1' } },
    { type: 'circle', radius: 3, color: 0xFFFFFF, alpha: 0.9, depth: 99,
      tween: { alpha: 0, scale: 2, duration: 220, ease: 'Power1' },
      scatter: { count: 5, radius: 26 } },
  ],
};

export const DEFAULT_COMBAT_VFX: VFXEffectConfig[] = [
  HIT_PHYSICAL,
  HIT_FIRE,
  HIT_ICE,
  HIT_LIGHTNING,
  HIT_HOLY,
  HIT_ARCANE,
  HIT_NATURE,
  HIT_SHADOW,
  HIT_HEAL,
  HIT_CRITICAL,
  CRIT_OVERLAY,
];
